/**
 * =================================================================
 * SCRIPT-NAME:   SendEmail.js
 * BESCHREIBUNG:   UI-Komponente zum Filtern von Kontakten und Versenden
 * von Massen-E-Mails.
 *
 * VERSION:       v1.15.i1
 * DATUM:         2026-06-17
 *
 * ÄNDERUNGEN ZUR VORVERSION (v1.14.i1):
 * - "📊 Versand-Statistik" ebenfalls ein-/ausklappbar, default zu.
 *
 * VORVERSION (v1.14.i1):
 * - "🧾 Versand-Protokolle" ein-/ausklappbar (klickbarer Header mit
 *   Pfeil), standardmäßig eingeklappt.
 *
 * VORVERSION (v1.13.i1):
 * - Interner Titel pro Sendung: Eingabefeld im Editor; Spalte „Titel" in
 *   der Statistik (Reihenfolge Datum·Titel·Betreff·Gesendet·Klicks·
 *   Klicker), inline editierbar (update_mailing.php).
 * - Sendung löschen: rotes ✕ rechts (mit Rückfrage, delete_mailing.php).
 *
 * VORVERSION (v1.12.i1):
 * - 20-Sekunden-Cooldown: nach dem Setzen der Bestätigung läuft ein
 *   Timer; erst danach wird der Senden-Button aktiv (gegen vorschnelles
 *   Senden). Button zeigt Countdown.
 *
 * VORVERSION (v1.11.i1):
 * - Pflicht-Bestätigung vor Versand: Checkbox "Bist du sicher? Betreff,
 *   Anrede und Text kontrolliert." muss gesetzt sein, sonst Senden-Button
 *   deaktiviert. Nach Versand zurückgesetzt.
 *
 * VORVERSION (v1.10.i1):
 * - Klick-Statistik: pro Mailing "👤 Wer (N)" -> zeigt namentlich, WER
 *   geklickt hat (Name/E-Mail/Anzahl/1. Klick/Links) aus get_mailings
 *   v1.1.i1 (clickers).
 *
 * VORVERSION (v1.9.i1):
 * - Neuer Abschnitt "🧾 Versand-Protokolle": listet die Lauf-Logs
 *   (get_send_log.php) mit OK/Fehler-Zahl; je Log per Klick der volle
 *   Inhalt (OK/FAIL pro Empfänger) einsehbar — ohne SSH.
 *
 * VORVERSION (v1.8.i1):
 * - Kontaktliste: Buttons "Alle auswählen" / "Alle abwählen" +
 *   Auswahl-Zähler (x/y ausgewählt) in der Überschrift.
 *
 * VORVERSION (v1.7.i1):
 * - Bewusst eingeschlossene Suppression-Status (Abgemeldet/Manuell
 *   gesperrt) werden als `allowReasons` an send_bulk_email.php (v1.5.i1)
 *   übergeben -> dürfen trotz Abmeldung angeschrieben werden. Beim Laden
 *   erscheint dann eine deutliche Warnung.
 *
 * VORVERSION (v1.6.i1):
 * - Tri-State-Filter: jeder Filter/Tag schaltet per Klick durch
 *   neutral -> ✓ einschließen -> ✕ ausschließen. Ausschluss geht als
 *   `exclude`-Array ans Backend (get_filtered_contacts.php v1.5.i1,
 *   AND NOT). Ermöglicht z. B. "Abgemeldet AND NOT Coaching".
 *
 * VORVERSION (v1.5.i1):
 * - Obere Filter ersetzt durch "Email-Status" (Abgemeldet, Manuell
 *   gesperrt); Funnel-Filter (IT/EG/Coaching) entfernt. Backend:
 *   get_filtered_contacts.php v1.4.i1.
 *
 * VORVERSION (v1.4.i1):
 * - Neuer Abschnitt "Versand-Statistik (Klicks)": lädt vergangene
 *   Versandläufe via get_mailings.php, zeigt Gesendet/Klicks/Klicker
 *   und je Mailing eine aufklappbare Link-Aufschlüsselung.
 * - Erfolgsmeldung zeigt jetzt sent/failed/suppressed aus der API.
 *
 * VORVERSION (v1.3.i1):
 * - Mehrfachauswahl der Filter über Checkboxen statt Single-Select.
 * - UND/ODER-Verknüpfung der ausgewählten Filter.
 * - Neue Filter: 'coaching_kunde' und 'abgemeldet'.
 * - Eigener "Kontakte laden"-Button statt Auto-Fetch bei Auswahl.
 * =================================================================
 */
import React, { useState, useEffect } from 'react';
import BackButton from "./BackButton";
import "./AdminDashboard.css";
import "./Contacts.css";
import "./SendEmail.css";

export default function SendEmail() {
  // Tri-State pro Filter/Tag: key -> 'include' | 'exclude' (fehlt = neutral)
  const [filterState, setFilterState] = useState({});
  const [logic, setLogic] = useState('AND');
  const [availableTags, setAvailableTags] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState({});
  const [title, setTitle] = useState(''); // interner Strategie-Titel der Sendung
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [message, setMessage] = useState('');
  const [editingTitle, setEditingTitle] = useState(null); // mailing id im Bearbeiten-Modus
  const [editTitleValue, setEditTitleValue] = useState('');
  const [isLoading, setIsLoading] = useState(false); // Während Kontakte geladen werden
  const [isSending, setIsSending] = useState(false); // To disable button during sending
  const [mailings, setMailings] = useState([]); // Versandläufe + Klick-Statistik
  const [expandedMailing, setExpandedMailing] = useState(null); // aufgeklappte Link-Liste
  const [expandedClickers, setExpandedClickers] = useState(null); // aufgeklappte Klicker-Liste
  // Bewusst eingeschlossene Suppression-Gründe der geladenen Liste (Versand-Block aufheben)
  const [allowReasons, setAllowReasons] = useState([]);
  const [sendLogs, setSendLogs] = useState([]); // Versand-Protokolle (Logdateien)
  const [openLog, setOpenLog] = useState(null); // { file, content }
  const [showLogs, setShowLogs] = useState(false); // Protokoll-Panel ein-/ausgeklappt (default zu)
  const [showStats, setShowStats] = useState(false); // Statistik-Panel ein-/ausgeklappt (default zu)
  const [confirmed, setConfirmed] = useState(false); // Pflicht-Bestätigung vor Versand
  const [countdown, setCountdown] = useState(0); // 20-Sek-Cooldown nach Bestätigung
  const token = localStorage.getItem("token");

  // Nach dem Setzen der Bestätigung 20 Sek. Cooldown, erst dann Senden aktiv
  useEffect(() => {
    if (!confirmed) { setCountdown(0); return; }
    setCountdown(20);
    const iv = setInterval(() => {
      setCountdown(c => {
        if (c <= 1) { clearInterval(iv); return 0; }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [confirmed]);

  // Versand-Protokolle (Logdateien) laden
  const loadSendLogs = React.useCallback(() => {
    fetch("https://freiseinapp.de/api/get_send_log.php", {
        method: "GET",
        headers: { "Authorization": `Bearer ${token}` }
    })
    .then(r => r.json())
    .then(d => { if (d.success) setSendLogs(d.logs); })
    .catch(() => { /* optional */ });
  }, [token]);

  const openSendLog = (file) => {
    if (openLog && openLog.file === file) { setOpenLog(null); return; }
    fetch(`https://freiseinapp.de/api/get_send_log.php?file=${encodeURIComponent(file)}`, {
        method: "GET",
        headers: { "Authorization": `Bearer ${token}` }
    })
    .then(r => r.json())
    .then(d => { if (d.success) setOpenLog({ file: d.file, content: d.content }); })
    .catch(() => { /* optional */ });
  };

  useEffect(() => { loadSendLogs(); }, [loadSendLogs]);

  // Versand-Statistik (Klicks) laden
  const loadMailings = React.useCallback(() => {
    fetch("https://freiseinapp.de/api/get_mailings.php", {
        method: "GET",
        headers: { "Authorization": `Bearer ${token}` }
    })
    .then(response => response.json())
    .then(data => { if (data.success) setMailings(data.mailings); })
    .catch(() => { /* Statistik optional */ });
  }, [token]);

  useEffect(() => { loadMailings(); }, [loadMailings]);

  // Titel einer Sendung speichern (nachträglich editierbar)
  const saveMailingTitle = (id) => {
    const newTitle = editTitleValue;
    fetch("https://freiseinapp.de/api/update_mailing.php", {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ id, title: newTitle })
    })
    .then(r => r.json())
    .then(d => {
        if (d.success) {
            setMailings(prev => prev.map(m => m.id === id ? { ...m, title: newTitle } : m));
            setEditingTitle(null);
        } else {
            setMessage(`❌ Titel konnte nicht gespeichert werden: ${d.message}`);
        }
    })
    .catch(() => setMessage('❌ Netzwerkfehler beim Speichern des Titels.'));
  };

  // Sendung löschen (mit Rückfrage — unumkehrbar)
  const deleteMailing = (id, label) => {
    if (!window.confirm(`Sendung „${label || ('#' + id)}" wirklich löschen? Das entfernt auch ihre Klick-Daten und kann nicht rückgängig gemacht werden.`)) return;
    fetch("https://freiseinapp.de/api/delete_mailing.php", {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ id })
    })
    .then(r => r.json())
    .then(d => {
        if (d.success) {
            setMailings(prev => prev.filter(m => m.id !== id));
            setMessage(`🗑️ Sendung gelöscht.`);
        } else {
            setMessage(`❌ Löschen fehlgeschlagen: ${d.message}`);
        }
    })
    .catch(() => setMessage('❌ Netzwerkfehler beim Löschen.'));
  };

  const filterOptions = [
    { value: 'abgemeldet', label: 'Abgemeldet (Newsletter abbestellt)' },
    { value: 'manuell_gesperrt', label: 'Manuell gesperrt' },
  ];

  // Verfügbare Tags einmalig laden (für dynamische Tag-Filter)
  useEffect(() => {
    fetch("https://freiseinapp.de/api/get_tags.php", {
        method: "GET",
        headers: { "Authorization": `Bearer ${token}` }
    })
    .then(response => response.json())
    .then(data => {
        if (data.success) setAvailableTags(data.tags);
    })
    .catch(() => { /* Tags optional – Standardfilter funktionieren weiterhin */ });
  }, [token]);

  // Klick schaltet durch: neutral -> einschließen -> ausschließen -> neutral
  const cycleFilter = (value) => {
    setFilterState(prev => {
      const cur = prev[value];
      const next = cur === undefined ? 'include' : cur === 'include' ? 'exclude' : undefined;
      const copy = { ...prev };
      if (next === undefined) { delete copy[value]; } else { copy[value] = next; }
      return copy;
    });
  };

  const handleLoadContacts = () => {
    const includeFilters = Object.keys(filterState).filter(key => filterState[key] === 'include');
    const excludeFilters = Object.keys(filterState).filter(key => filterState[key] === 'exclude');
    setContacts([]);
    setSelectedContacts({});

    if (includeFilters.length === 0 && excludeFilters.length === 0) {
      setMessage('❌ Bitte mindestens einen Filter ein- oder ausschließen.');
      return;
    }

    // Bewusst eingeschlossene Suppression-Status -> dürfen beim Versand angeschrieben werden
    const ar = [];
    if (includeFilters.includes('abgemeldet')) ar.push('unsubscribe');
    if (includeFilters.includes('manuell_gesperrt')) ar.push('manual');
    setAllowReasons(ar);

    setIsLoading(true);
    const parts = [];
    if (includeFilters.length) parts.push(`${includeFilters.length}× einschl. (${logic === 'AND' ? 'UND' : 'ODER'})`);
    if (excludeFilters.length) parts.push(`${excludeFilters.length}× ausschl.`);
    setMessage(`Lade Kontakte (${parts.join(', ')})...`);

    fetch("https://freiseinapp.de/api/get_filtered_contacts.php", {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({ filters: includeFilters, exclude: excludeFilters, logic: logic })
    })
    .then(response => response.json())
    .then(data => {
        if (data.success) {
            setContacts(data.contacts);
            const initialSelection = data.contacts.reduce((acc, contact) => {
                acc[contact.id] = true;
                return acc;
            }, {});
            setSelectedContacts(initialSelection);
            const warn = ar.length
              ? ' ⚠️ Achtung: Abgemeldete/Gesperrte sind bewusst eingeschlossen und werden trotz Abmeldung angeschrieben.'
              : '';
            setMessage(`${data.contacts.length} Kontakte geladen. Bitte Auswahl prüfen.${warn}`);
        } else {
            setMessage(`❌ Fehler: ${data.message}`);
        }
    })
    .catch(() => {
        setMessage('❌ Netzwerkfehler beim Laden der Kontakte.');
    })
    .finally(() => {
        setIsLoading(false);
    });
  };

  const handleCheckboxChange = (contactId) => {
    setSelectedContacts(prev => ({
      ...prev,
      [contactId]: !prev[contactId]
    }));
  };

  const selectAllContacts = () => {
    setSelectedContacts(contacts.reduce((acc, c) => { acc[c.id] = true; return acc; }, {}));
  };

  const deselectAllContacts = () => {
    setSelectedContacts({});
  };

  const handleSendEmails = () => {
    const finalContactIds = Object.keys(selectedContacts).filter(id => selectedContacts[id]);
    if (finalContactIds.length === 0) {
      setMessage('❌ Bitte wählen Sie mindestens einen Kontakt aus.');
      return;
    }
    if (!subject || !body) {
      setMessage('❌ Bitte geben Sie einen Betreff und einen Nachrichtentext ein.');
      return;
    }

    setIsSending(true);
    setMessage(`Sende E-Mails an ${finalContactIds.length} Kontakte...`);
    
    // --- NEU: Echter API-Aufruf an das Backend ---
    fetch("https://freiseinapp.de/api/send_bulk_email.php", {
        method: "POST",
        headers: {
            "Authorization": `Bearer ${token}`,
            "Content-Type": "application/json"
        },
        body: JSON.stringify({
            contactIds: finalContactIds,
            subject: subject,
            title: title,
            body: body,
            allowReasons: allowReasons
        })
    })
    .then(response => response.json())
    .then(data => {
        if (data.success) {
            setMessage(`✅ Versand abgeschlossen: ${data.sentCount} gesendet${data.failedCount ? `, ${data.failedCount} fehlgeschlagen` : ''}${data.suppressedCount ? `, ${data.suppressedCount} abgemeldet/gesperrt ausgeschlossen` : ''}.`);
            loadMailings(); // Statistik aktualisieren
            loadSendLogs(); // Protokolle aktualisieren
            setConfirmed(false); // Bestätigung für nächsten Versand zurücksetzen
            setTitle(''); // Titel für nächsten Versand zurücksetzen
        } else {
            setMessage(`❌ Fehler beim E-Mail-Versand: ${data.message}`);
        }
    })
    .catch(() => {
        setMessage('❌ Netzwerkfehler beim Senden der E-Mails.');
    })
    .finally(() => {
        setIsSending(false); // Re-enable the button after the process is complete
    });
    // --- ENDE NEU ---
  };

  return (
    <div className="dashboard-page-wrapper">
      <div className="dashboard-content-box">
        <header className="dashboard-header">
          <h2 className="dashboard-title">✉️ E-Mail senden</h2>
        </header>

        {message && <p className={message.startsWith('❌') ? "contacts-error" : "contacts-success"}>{message}</p>}

        <div className="send-email-container">
          {/* SECTION 1: FILTERING */}
          <section className="email-section">
            <h3>1. Kontakte auswählen</h3>
            <div className="filter-controls">
              <p className="filter-hint">
                Klick schaltet durch: <span className="chip-legend include">✓ einschließen</span>
                {' '}<span className="chip-legend exclude">✕ ausschließen</span>
                {' '}<span className="chip-legend">neutral</span>
              </p>

              <label>Email-Status:</label>
              <div className="filter-chip-list">
                {filterOptions.map(option => {
                  const st = filterState[option.value];
                  return (
                    <button
                      type="button"
                      key={option.value}
                      className={`filter-chip ${st || ''}`}
                      onClick={() => cycleFilter(option.value)}
                    >
                      <span className="chip-mark">{st === 'include' ? '✓' : st === 'exclude' ? '✕' : ''}</span>
                      {option.label}
                    </button>
                  );
                })}
              </div>

              {availableTags.length > 0 && (
                <div className="filter-tags">
                  <span className="filter-tags-title">Tags ({availableTags.length}):</span>
                  <div className="filter-chip-list filter-tags-list">
                    {availableTags.map(tag => {
                      const key = `tag:${tag.name}`;
                      const st = filterState[key];
                      return (
                        <button
                          type="button"
                          key={key}
                          className={`filter-chip ${st || ''}`}
                          onClick={() => cycleFilter(key)}
                        >
                          <span className="chip-mark">{st === 'include' ? '✓' : st === 'exclude' ? '✕' : ''}</span>
                          {tag.name} ({tag.anzahl})
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="filter-logic">
                <span>Verknüpfung:</span>
                <label>
                  <input
                    type="radio"
                    name="filter-logic"
                    value="AND"
                    checked={logic === 'AND'}
                    onChange={() => setLogic('AND')}
                  /> UND (alle Filter müssen zutreffen)
                </label>
                <label>
                  <input
                    type="radio"
                    name="filter-logic"
                    value="OR"
                    checked={logic === 'OR'}
                    onChange={() => setLogic('OR')}
                  /> ODER (mind. ein Filter trifft zu)
                </label>
              </div>

              <button
                type="button"
                className="load-contacts-button"
                onClick={handleLoadContacts}
                disabled={isLoading}
              >
                {isLoading ? 'Lade...' : 'Kontakte laden'}
              </button>
            </div>
            {contacts.length > 0 && (
              <div className="contact-list">
                <div className="contact-list-header">
                  <h4>Kontakte für den Versand ({Object.values(selectedContacts).filter(Boolean).length}/{contacts.length} ausgewählt):</h4>
                  <div className="contact-list-actions">
                    <button type="button" className="select-action" onClick={selectAllContacts}>Alle auswählen</button>
                    <button type="button" className="select-action" onClick={deselectAllContacts}>Alle abwählen</button>
                  </div>
                </div>
                {contacts.map(contact => (
                  <div key={contact.id} className="contact-list-item">
                    <input
                      type="checkbox"
                      id={`contact-${contact.id}`}
                      checked={!!selectedContacts[contact.id]}
                      onChange={() => handleCheckboxChange(contact.id)}
                    />
                    <label htmlFor={`contact-${contact.id}`}>
                      {contact.vorname} {contact.nachname} ({contact.email})
                    </label>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* SECTION 2: EMAIL EDITOR */}
          <section className="email-section">
            <h3>2. E-Mail verfassen</h3>
            <div className="email-form">
              <label htmlFor="title">Titel (intern, nur für die Statistik – nicht für den Empfänger sichtbar):</label>
              <input
                type="text"
                id="title"
                placeholder="z. B. Buchstart Serie 1 – Mail 2 / Abgemeldete"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <label htmlFor="subject">Betreff:</label>
              <input
                type="text"
                id="subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
              <label htmlFor="body">Nachricht (Platzhalter wie {`{vorname}`} werden auf dem Server ersetzt):</label>
              <textarea
                id="body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
              ></textarea>
            </div>
          </section>

          {/* SECTION 3: SEND BUTTON */}
          <section>
            <label className="send-confirm">
              <input
                type="checkbox"
                checked={confirmed}
                onChange={(e) => setConfirmed(e.target.checked)}
              />
              <span>Bist du sicher? Betreff, Anrede und Text kontrolliert.</span>
            </label>
            <button onClick={handleSendEmails} className="send-button" disabled={isSending || !confirmed || countdown > 0}>
              {isSending
                ? 'Sende...'
                : (confirmed && countdown > 0)
                  ? `Bitte kurz prüfen … aktiv in ${countdown}s`
                  : `E-Mails jetzt an ${Object.values(selectedContacts).filter(Boolean).length} Kontakte senden`}
            </button>
          </section>

          {/* SECTION 4: KLICK-STATISTIK */}
          {mailings.length > 0 && (
            <section className="email-section mailing-stats">
              <h3
                className="collapsible-header"
                onClick={() => setShowStats(s => !s)}
                title={showStats ? 'Einklappen' : 'Ausklappen'}
              >
                <span className="collapse-arrow">{showStats ? '▼' : '▶'}</span> 📊 Versand-Statistik (Klicks) ({mailings.length})
              </h3>
              {showStats && (
              <table className="mailing-stats-table">
                <thead>
                  <tr>
                    <th>Datum</th>
                    <th>Titel</th>
                    <th>Betreff</th>
                    <th>Gesendet</th>
                    <th>Klicks</th>
                    <th>Klicker</th>
                    <th></th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {mailings.map(m => (
                    <React.Fragment key={m.id}>
                      <tr>
                        <td>{new Date(m.created_at.replace(' ', 'T')).toLocaleString('de-DE')}</td>
                        <td className="title-cell">
                          {editingTitle === m.id ? (
                            <span className="title-edit">
                              <input
                                type="text"
                                value={editTitleValue}
                                autoFocus
                                onChange={(e) => setEditTitleValue(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') saveMailingTitle(m.id); if (e.key === 'Escape') setEditingTitle(null); }}
                              />
                              <button type="button" className="select-action" onClick={() => saveMailingTitle(m.id)}>✓</button>
                              <button type="button" className="select-action" onClick={() => setEditingTitle(null)}>✕</button>
                            </span>
                          ) : (
                            <span
                              className="title-display"
                              title="Klicken zum Bearbeiten"
                              onClick={() => { setEditingTitle(m.id); setEditTitleValue(m.title || ''); }}
                            >
                              {m.title ? m.title : <span className="title-empty">+ Titel</span>}
                            </span>
                          )}
                        </td>
                        <td>{m.subject}</td>
                        <td>{m.sent_count}{m.failed_count > 0 && <span className="stat-fail"> (−{m.failed_count})</span>}</td>
                        <td>{m.total_clicks}</td>
                        <td>{m.unique_clickers}</td>
                        <td className="stat-actions">
                          {m.links.length > 0 && (
                            <button
                              type="button"
                              className="stat-toggle"
                              onClick={() => setExpandedMailing(expandedMailing === m.id ? null : m.id)}
                            >
                              {expandedMailing === m.id ? 'Links ausblenden' : `${m.links.length} Link(s)`}
                            </button>
                          )}
                          {m.clickers && m.clickers.length > 0 && (
                            <button
                              type="button"
                              className="stat-toggle"
                              onClick={() => setExpandedClickers(expandedClickers === m.id ? null : m.id)}
                            >
                              {expandedClickers === m.id ? 'schließen' : `👤 Wer (${m.clickers.length})`}
                            </button>
                          )}
                        </td>
                        <td className="delete-cell">
                          <button
                            type="button"
                            className="delete-mailing"
                            title="Sendung löschen"
                            onClick={() => deleteMailing(m.id, m.title || m.subject)}
                          >✕</button>
                        </td>
                      </tr>
                      {expandedMailing === m.id && m.links.length > 0 && (
                        <tr className="mailing-links-row">
                          <td colSpan={8}>
                            <ul className="mailing-links">
                              {m.links.map((l, i) => (
                                <li key={i}>
                                  <span className="link-clicks">{l.clicks}×</span>
                                  <a href={l.url} target="_blank" rel="noopener noreferrer">{l.url}</a>
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      )}
                      {expandedClickers === m.id && m.clickers && m.clickers.length > 0 && (
                        <tr className="mailing-links-row">
                          <td colSpan={8}>
                            <table className="clickers-table">
                              <thead>
                                <tr><th>Name</th><th>E-Mail</th><th>Klicks</th><th>1. Klick</th><th>Links</th></tr>
                              </thead>
                              <tbody>
                                {m.clickers.map((c, i) => (
                                  <tr key={i}>
                                    <td>{`${c.vorname || ''} ${c.nachname || ''}`.trim() || '—'}</td>
                                    <td>{c.email || '—'}</td>
                                    <td>{c.clicks}</td>
                                    <td>{c.first_click}</td>
                                    <td>{(c.urls || []).map((u, j) => (
                                      <div key={j}><a href={u} target="_blank" rel="noopener noreferrer">{u}</a></div>
                                    ))}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              )}
            </section>
          )}

          {/* SECTION 5: VERSAND-PROTOKOLLE (Logs) */}
          {sendLogs.length > 0 && (
            <section className="email-section send-logs">
              <h3
                className="collapsible-header"
                onClick={() => setShowLogs(s => !s)}
                title={showLogs ? 'Einklappen' : 'Ausklappen'}
              >
                <span className="collapse-arrow">{showLogs ? '▼' : '▶'}</span> 🧾 Versand-Protokolle ({sendLogs.length})
              </h3>
              {showLogs && (
              <table className="mailing-stats-table">
                <thead>
                  <tr>
                    <th>Zeit</th>
                    <th>Betreff</th>
                    <th>OK</th>
                    <th>Fehler</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sendLogs.map(l => (
                    <React.Fragment key={l.file}>
                      <tr>
                        <td>{l.mtime}</td>
                        <td>{l.subject || '—'}</td>
                        <td>{l.ok}</td>
                        <td>{l.fail > 0 ? <span className="stat-fail">{l.fail}</span> : 0}</td>
                        <td>
                          <button type="button" className="stat-toggle" onClick={() => openSendLog(l.file)}>
                            {openLog && openLog.file === l.file ? 'schließen' : 'Log ansehen'}
                          </button>
                        </td>
                      </tr>
                      {openLog && openLog.file === l.file && (
                        <tr className="mailing-links-row">
                          <td colSpan={5}>
                            <pre className="send-log-content">{openLog.content}</pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
              )}
            </section>
          )}
        </div>

        <div className="dashboard-footer">
          <BackButton to="/admin" />
        </div>
      </div>
    </div>
  );
}
